import { Dispatch } from 'redux'

import {
  getAll,
  create,
  duplicate,
  update,
  destroy,
  upload,
  updateCompanies
} from 'redux/api/EconomySets'
import { getAll as getAllKpiVariableOptions } from 'redux/api/KpiVariableOptions'

import { useThunkActions } from 'redux/actions/useThunkActions'
import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import * as kpiVariableOptionActions from 'redux/actions/KpiVariableOptions/actions'

import { SnackbarMessage } from 'types/GlobalSnackbar'

import * as actions from './actions'

function tryGetAllEconomySets() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

function tryCreateEconomySet(
  name: string,
  csvFile: Blob | null,
  companyIds: string[] | undefined,
  callback?: (err?: boolean, id?: string) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(name, csvFile, companyIds)
      .then((response) => {
        dispatch(actions.createSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsCreated))

        callback?.(false, response.data.data.id)
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsCreatedFailed)
        )

        callback?.(true)
      })
  }
}

function tryDuplicateEconomySet(
  id: string,
  name: string,
  includeAccountReferences: boolean,
  companyIds: string[] | undefined,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.duplicate())

    duplicate(id, name, includeAccountReferences, companyIds)
      .then((response) => {
        dispatch(actions.duplicateSuccess(response.data.data))

        /*
         * The duplicate has account references of its own, so the fetched
         * options no longer cover every chart of accounts.
         */
        if (includeAccountReferences) {
          getAllKpiVariableOptions()
            .then((optionsResponse) => {
              dispatch(
                kpiVariableOptionActions.getAllSuccess(
                  optionsResponse.data.data
                )
              )
            })
            .catch(() => {
              dispatch(kpiVariableOptionActions.getAllFailed())
            })
        }

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsCreated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.duplicateFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsCreatedFailed)
        )

        callback?.(true)
      })
  }
}

function tryUpdateEconomySet(
  id: string,
  name: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, name)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdatedFailed)
        )

        callback?.(true)
      })
  }
}

function tryUpdateEconomySetCompanies(
  id: string,
  companyIds: string[],
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateCompanies())

    updateCompanies(id, companyIds)
      .then((response) => {
        dispatch(actions.updateCompaniesSuccess(response.data.data))

        // A company holds one company-specific chart of accounts, so the
        // companies may have moved off another one.
        getAll()
          .then((allResponse) => {
            dispatch(actions.getAllSuccess(allResponse.data.data))
          })
          .catch(() => {
            dispatch(actions.getAllFailed())
          })

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateCompaniesFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdatedFailed)
        )

        callback?.(true)
      })
  }
}

function tryDeleteEconomySet(id: string, callback?: (err?: boolean) => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess({ id }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsDeleted))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsDeletedFailed)
        )

        callback?.(true)
      })
  }
}

function tryUploadEconomySet(
  id: string,
  csvFile: Blob,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.upload())

    upload(id, csvFile)
      .then((response) => {
        dispatch(actions.uploadSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.uploadFailed())

        dispatch(
          toggleSnackbarOpen(SnackbarMessage.ChartOfAccountsUpdatedFailed)
        )

        callback?.(true)
      })
  }
}

export const useEconomySetActions = () => {
  return {
    tryCreateEconomySet: useThunkActions(tryCreateEconomySet),
    tryDeleteEconomySet: useThunkActions(tryDeleteEconomySet),
    tryDuplicateEconomySet: useThunkActions(tryDuplicateEconomySet),
    tryGetAllEconomySets: useThunkActions(tryGetAllEconomySets),
    tryUpdateEconomySet: useThunkActions(tryUpdateEconomySet),
    tryUpdateEconomySetCompanies: useThunkActions(tryUpdateEconomySetCompanies),
    tryUploadEconomySet: useThunkActions(tryUploadEconomySet)
  }
}
